import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@reflex/ui/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import type { WsConnectionState } from '@/lib/socketManager';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useVersionStatus } from '@/hooks/ui/useVersionCheck';
import { stateConfig } from './connectionState';
import { StatusPanel } from './StatusPanel';

interface ConnectionIndicatorProps {
  state: WsConnectionState;
  /** When true, only show the status dot; label appears in popover */
  iconOnly?: boolean;
}

export function ConnectionIndicator({ state, iconOnly }: ConnectionIndicatorProps) {
  const versionStatus = useVersionStatus();
  const updatePending = state === 'open' && versionStatus.updateAvailable;

  const dotColor = updatePending ? 'bg-status-warning animate-pulse' : stateConfig[state].color;
  const label = updatePending ? 'Update available' : stateConfig[state].label;
  // A healthy, connected socket renders as a calm glowing core. The ping ring
  // only flashes briefly on the *transition* into connected — a confirmation
  // beat, not a permanent strobe — then settles back to steady state.
  const isLive = state === 'open' && !updatePending;

  // Detect the rising edge into "connected" by comparing against previous state
  // during render (React's "adjust state on prop change" pattern) so the flash
  // turns on without a setState-in-effect cascade; an effect only owns the async
  // clear timer that returns the dot to its calm steady state.
  const [flashConnected, setFlashConnected] = useState(false);
  const [prevIsLive, setPrevIsLive] = useState(isLive);
  if (isLive !== prevIsLive) {
    setPrevIsLive(isLive);
    setFlashConnected(isLive);
  }
  useEffect(() => {
    if (!flashConnected) return;
    const timer = setTimeout(() => setFlashConnected(false), 1500);
    return () => clearTimeout(timer);
  }, [flashConnected]);

  const { data: status } = useQuery({
    queryKey: queryKeys.serverStatus,
    queryFn: api.getStatus,
    staleTime: 60_000,
    enabled: state === 'open',
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center cursor-pointer hover:opacity-80 transition-opacity rounded-md',
            iconOnly ? 'p-0.5' : 'gap-2 px-1.5 py-0.5 -mx-0.5',
          )}
          aria-label={label}
        >
          <span
            className="relative flex h-2 w-2 shrink-0 items-center justify-center"
            data-testid="connection-indicator-dot"
            data-state={updatePending ? 'update' : state}
          >
            {/* Soft glow halo — same hue as the core, blurred out behind it. */}
            <span
              aria-hidden
              className={cn('absolute h-2 w-2 rounded-full opacity-70 blur-[3px]', dotColor)}
            />
            {/* Ping ring — a brief confirmation flash on connect, not steady state. */}
            {flashConnected && (
              <span
                aria-hidden
                className={cn(
                  'absolute h-2.5 w-2.5 rounded-full opacity-40 animate-ping',
                  dotColor,
                )}
              />
            )}
            {/* Core dot with a fine inner highlight so it catches light. */}
            <span
              className={cn(
                'relative h-1.5 w-1.5 rounded-full ring-1 ring-inset ring-white/30',
                dotColor,
              )}
            />
          </span>
          {!iconOnly && <span className="text-xs text-muted-foreground">{label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="w-auto p-4">
        <StatusPanel state={state} status={status} />
      </PopoverContent>
    </Popover>
  );
}
